import { useState } from "react";
import { ToastConfig } from "../components/common/ToastConfig";
import { ClientTaskTypeCategory } from "../types/dtos/tasks/advanced-tasks/ClientTaskTypeCategory";
import { ClientTaskType } from "../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { advancedTaskHelper } from "../helpers";

/**
 * Custom hook for task management functionality
 * Provides state and handlers for task popups, success messages, etc.
 */
const useTaskManagement = (
  taskType: ClientTaskType | null,
  onRefreshNeeded?: (userId: number) => void
) => {
  // Task popup state
  const [isExistingTaskPopupOpen, setIsExistingTaskPopupOpen] =
    useState<boolean>(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedUserCategories, setSelectedUserCategories] = useState<
    ClientTaskTypeCategory[] | null
  >(null);
  const [isSelectedTaskLocked, setIsSelectedTaskLocked] =
    useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // Toast state
  const [toastConfig, setToastConfig] = useState<ToastConfig>(
    ToastConfig.hidden()
  );

  // Get task type singular name for toast messages
  const getTaskTypeSingularName = (): string => {
    if (taskType && taskType.singularNameTranslationKeyIdentifier) {
      return advancedTaskHelper.ToLowerCase(
        taskType.singularNameTranslationKeyIdentifier
      );
    }
    return "task";
  };

  // Handle task popup changes
  const handleTaskPopupOpenChange = (open: boolean) => {
    setIsExistingTaskPopupOpen(open);
    if (!open) {
      // Signal parent that a refresh is needed for this user
      if (onRefreshNeeded && selectedUserId) {
        onRefreshNeeded(selectedUserId);
      }

      // Reset state
      resetTaskState();
    }
  };

  // Reset task state
  const resetTaskState = () => {
    setSelectedTaskId(null);
    setSelectedUserCategories(null);
    setIsSelectedTaskLocked(false);
    setSelectedUserId(null);
  };

  // Success handlers for task actions
  const handleSuccessfulUpdate = () => {
    handleTaskPopupOpenChange(false);
    setToastConfig(
      ToastConfig.success(
        "Task Updated",
        `You have successfully updated the ${getTaskTypeSingularName()}`
      )
    );
  };

  const handleSuccessfulCancel = () => {
    handleTaskPopupOpenChange(false);
    setToastConfig(
      ToastConfig.success(
        "Task Cancelled",
        `You have successfully cancelled the ${getTaskTypeSingularName()}`
      )
    );
  };

  const handleSuccessfulClose = () => {
    handleTaskPopupOpenChange(false);
    setToastConfig(
      ToastConfig.success(
        "Task Closed",
        `You have successfully closed the ${getTaskTypeSingularName()}`
      )
    );
  };

  const handleSuccessfulReOpen = () => {
    handleTaskPopupOpenChange(false);
    setToastConfig(
      ToastConfig.success(
        "Task Reopened",
        `You have successfully re-opened the ${getTaskTypeSingularName()}`
      )
    );
  };

  const handleSuccessfulSavedForLater = () => {
    handleTaskPopupOpenChange(false);
    setToastConfig(
      ToastConfig.success(
        "Draft Response Saved",
        `You have successfully created a draft response against the ${getTaskTypeSingularName()}. Remember to go back and finalise changes before submitting.`
      )
    );
  };

  // Open task popup
  const openTaskPopup = (
    taskId: string,
    categories: ClientTaskTypeCategory[] | null,
    isLocked: boolean,
    userId: number
  ) => {
    setSelectedTaskId(taskId);
    setSelectedUserCategories(categories);
    setIsSelectedTaskLocked(isLocked);
    setSelectedUserId(userId);
    setIsExistingTaskPopupOpen(true);
  };

  return {
    // State
    isExistingTaskPopupOpen,
    selectedTaskId,
    selectedUserCategories,
    isSelectedTaskLocked,
    selectedUserId,
    toastConfig,

    // Methods
    setToastConfig,
    handleTaskPopupOpenChange,
    handleSuccessfulUpdate,
    handleSuccessfulCancel,
    handleSuccessfulClose,
    handleSuccessfulReOpen,
    handleSuccessfulSavedForLater,
    openTaskPopup,
    resetTaskState,
  };
};

export default useTaskManagement;
